import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Container,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  CardContent,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Science as ScienceIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import axios from "axios";
import {
  decryptText,
  decryptAppId,
} from "../../../utils/encryption";
import PageTitle from "../../../components/PageTitle";
import useAssessmentTracking from '../../../hooks/useAssessmentTracking';
import { LocationWarningDialog, HourlyAttendanceDialog } from '../../../components/AssessmentTrackingDialogs';
import s3Utils from '../../../utils/s3Utils';

const EquipmentDetails = () => {
  const location = useLocation();

  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get("orgId");
    if (encryptedOrgId) {
      try {
        const decryptedOrgId = decryptText(encryptedOrgId);
        console.log("📍 Using decrypted orgId from URL query:", decryptedOrgId);
        return decryptedOrgId;
      } catch (error) {
        return encryptedOrgId;
      }
    }
    return null;
  };

  // Function to get appId from URL query parameters with decryption
  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get("appId");
    if (encryptedAppId) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        return decryptedAppId;
      } catch (error) {
        return encryptedAppId;
      }
    }
    return null;
  };

  // Function to get assessmentId from URL query parameters with decryption
  const getAssessmentIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentId = searchParams.get("assessmentId");
    if (encryptedAssessmentId) {
      try {
        const decryptedAssessmentId = decryptText(encryptedAssessmentId);
        console.log(
          "📍 Using decrypted assessmentId from URL query:",
          decryptedAssessmentId
        );
        return decryptedAssessmentId;
      } catch (error) {
        return encryptedAssessmentId;
      }
    }
    return null;
  };

  // Function to get assessorId from URL query parameters with decryption
  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get("assessorId");
    if (encryptedAssessorId) {
      try {
        const decryptedAssessorId = decryptText(encryptedAssessorId);
        console.log(
          "📍 Using decrypted assessorId from URL query:",
          decryptedAssessorId
        );
        return decryptedAssessorId;
      } catch (error) {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getOrgId = () => {
    const queryOrgId = getOrgIdFromQuery();
    if (queryOrgId) {
      return queryOrgId;
    }
    return null;
  };

  const getAppId = () => {
    const queryAppId = getAppIdFromQuery();
    if (queryAppId) {
      return queryAppId;
    }
    return null;
  };

  const getAssessmentId = () => {
    const queryAssessmentId = getAssessmentIdFromQuery();
    if (queryAssessmentId) {
      return queryAssessmentId;
    }
    return null;
  };

  const getAssessorId = () => {
    const queryAssessorId = getAssessorIdFromQuery();
    if (queryAssessorId) {
      return queryAssessorId;
    }
    return null;
  };

  const [orgId, setOrgId] = useState(getOrgId());
  const [appId, setAppId] = useState(getAppId());
  const [assessmentId, setAssessmentId] = useState(getAssessmentId());
  const [assessorId, setAssessorId] = useState(getAssessorId());
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Assessment tracking hook for location and hourly attendance
  const {
    locationWarningOpen,
    distanceMessage,
    attendanceCheckDialogOpen,
    attendanceCheckMessage,
    setLocationWarningOpen,
    setAttendanceCheckDialogOpen,
    checkHourlyAttendance,
  } = useAssessmentTracking({
    assessorId,
    appId,
    orgId,
  });
  
  // Equipment Verification State
  const [verificationData, setVerificationData] = useState([]);
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  // Equipment Details Dialog State
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  
  // Image Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  
  // NC Clause Statement State
  const [ncStatements, setNcStatements] = useState([]);
  const [ncStatementsLoading, setNcStatementsLoading] = useState(false);

  // Get URL parameters
  useEffect(() => {

    if (appId && orgId) {
      loadVerificationData(appId, orgId);
    }
  }, [appId, orgId, assessmentId, assessorId]);
  
  // Load Equipment Verification Data from API
  const loadVerificationData = async (appId, orgId) => {
    setVerificationLoading(true);
    try {
      const url = `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetVerifyEquipment?appId=${appId}&orgId=${orgId}&assessmentId=${assessmentId}&assessorId=${assessorId}`;
      const resp = await axios.get(url);

      if (resp.data.isstatus && Array.isArray(resp.data.equip)) {
        setVerificationData(resp.data.equip);
      } else {
        setVerificationData([]);
      }
    } catch (error) {
      console.error("Error loading equipment verification data:", error);
      showSnackbar("Error loading equipment verification data", "error");
      setVerificationData([]);
    } finally {
      setVerificationLoading(false);
    }
  };
  
  // Load NC Clause Statements from API
  const loadNCStatements = async (appId, orgId, sourceId) => {
    setNcStatementsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/assessment/nc-clause-statement?assessorId=${assessorId}&assessmentId=${assessmentId}&appId=${appId}&orgId=${orgId}&sourceId=${sourceId}&section=Equipment`;
      const resp = await axios.get(url);

      if (resp.data.success && Array.isArray(resp.data.data)) {
        setNcStatements(resp.data.data);
      } else {
        setNcStatements([]);
      }
    } catch (error) {
      console.error("Error loading NC clause statements:", error);
      setNcStatements([]);
    } finally {
      setNcStatementsLoading(false);
    }
  };

  // Show Snackbar
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  // Handle View Details
  const handleViewDetails = (equipment) => {
    setSelectedEquipment(equipment);
    setDetailsDialogOpen(true);
    // Load NC statements for this specific equipment
    if (equipment.id) {
      loadNCStatements(appId, orgId, equipment.id);
    }
  };

  // Handle Close Details Dialog
  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedEquipment(null);
  };

  // Lightbox handlers
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString || dateString === "-") return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Handle file view using s3Utils
  const handleViewFile = async (fileName) => {
  try {
    const cleanedFileName = fileName.startsWith("live/")
      ? fileName.replace(/^live\//, "")
      : fileName;

    const url = await s3Utils.generateSignedUrlForFilename(cleanedFileName);
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  } catch (error) {
    console.error("Error generating S3 URL:", error);
  }
};

  return (
    <>
    <PageTitle title="NABL | Equipment Details" />
    <Container maxWidth="xl" sx={{ py: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: "primary.main",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ScienceIcon sx={{ fontSize: 40 }} />
          Equipment Details
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          View equipment details for assessment
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Equipment Verification Status Table */}
      <Box sx={{ mt: 4 }}>
        {verificationLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : verificationData.length > 0 ? (
          <TableContainer component={Paper} variant="outlined" sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 50 }}>S No.</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 200 }}>Equipment Name</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 120 }}>Equipment ID</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 120 }}>UID</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 120 }}>Make & Model</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 100 }}>Working Status</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 120 }}>Verification Status</TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold", minWidth: 100 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {verificationData.map((item, index) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                        {item.nameOfEquipment || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.equipmentid || "-"}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: "medium" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.uid || "-"}
                        size="small"
                        color="primary"
                        variant="filled"
                        sx={{ fontWeight: "medium" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {item.makeCompany && item.model 
                          ? `${item.makeCompany} - ${item.model}`
                          : item.makeCompany || item.model || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={item.workingstatus || "-"}
                        color={item.workingstatus === "Working" ? "success" : "error"}
                        size="small"
                        sx={{ fontWeight: "medium" }}
                      />
                    </TableCell>
                    <TableCell>
                      {item.isverified ? (
                        <Chip
                          label={item.isverified}
                          color={item.isverified.toLowerCase() === "yes" ? "success" : "error"}
                          size="small"
                          sx={{ fontWeight: "medium" }}
                        />
                      ) : (
                        <Chip label="Pending" color="warning" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => handleViewDetails(item)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
            <ScienceIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary">
              No Equipment Verification Data
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Equipment verification status will appear here once available.
            </Typography>
          </Card>
        )}
      </Box>

      {/* Equipment Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #6366f1, #ec4899)",
            color: "white",
            py: 3,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ScienceIcon />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Equipment Details
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDetailsDialog}
              size="small"
              sx={{ color: "white" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedEquipment && (
            <Box>
              {/* Basic Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Equipment Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.nameOfEquipment || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Equipment ID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.equipmentid || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">UID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.uid || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Source ID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.sourceId || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Make/Company</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.makeCompany || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Model</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.model || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Serial No</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.serialNo || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Year of Make</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.yearOfMake || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Receipt Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedEquipment.receiptDate)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Date of Installation</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedEquipment.dateOfInstallation)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Date of Service</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedEquipment.dateofService)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Technical Specifications */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                    Technical Specifications
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Range</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.range || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Accuracy</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.accuracy || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Limit of Detection</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.limitOfDetection || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">IQC ID</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.iqC_Id || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Supporting Equipment</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.supportingEquipment || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Calibration Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                    Calibration Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Date of Last Calibration</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedEquipment.dateOfLastCalibration)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Calibration Due On</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(selectedEquipment.calibrationDueOn)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Calibrated By Type</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedEquipment.calibratedByType || "-"}
                          color={selectedEquipment.calibratedByType === "In-house" ? "primary" : "secondary"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Calibrated By</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.calibratedBy || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">In-house Calibration</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.inHouseCalibration || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">External Calibration Agency</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.externalCalibrationAgency || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Calibration Agency Name</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.calibratAgencyName || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Calibration Traceability</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.calibratTracibility || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Upload Documents */}
              {(selectedEquipment.iQuploadFile || selectedEquipment.oQuploadFile || selectedEquipment.pQuploadFile) && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    {/* <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                      Uploaded Documents
                    </Typography> */}
                    <Grid container spacing={2}>
                      {selectedEquipment.iQuploadFile && (
                        <Grid item xs={12}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <ImageIcon sx={{ color: "primary.main" }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary">IQ File</Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewFile(selectedEquipment.iQuploadFile)}
                            >
                              View
                            </Button>
                          </Box>
                        </Grid>
                      )}
                      {selectedEquipment.oQuploadFile && (
                        <Grid item xs={12}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <ImageIcon sx={{ color: "primary.main" }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary">OQ File</Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewFile(selectedEquipment.oQuploadFile)}
                            >
                              View
                            </Button>
                          </Box>
                        </Grid>
                      )}
                      {selectedEquipment.pQuploadFile && (
                        <Grid item xs={12}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                            <ImageIcon sx={{ color: "primary.main" }} />
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" color="text.secondary">PQ File</Typography>
                            </Box>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleViewFile(selectedEquipment.pQuploadFile)}
                            >
                              View
                            </Button>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {/* Verification Information */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                    Verification Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Verification Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedEquipment.isverified || "Pending"}
                          color={selectedEquipment.isverified?.toLowerCase() === "yes" ? "success" : "error"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Verification Date</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.verifydate ? formatDate(selectedEquipment.verifydate) : "-"}
                      </Typography>
                    </Grid>
                     <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Working Status</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedEquipment.workingstatus || "-"}
                          color={selectedEquipment.workingstatus === "Working" ? "success" : "error"}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Verification Remarks</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {selectedEquipment.verifyRemarks || "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Equipment Images */}
              {selectedEquipment.doc && selectedEquipment.doc.length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: "primary.main" }}>
                      Equipment Images ({selectedEquipment.doc.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                      {selectedEquipment.doc.map((image, imgIndex) => (
                        <EquipmentImage
                          key={imgIndex}
                          doc={image}
                          index={imgIndex}
                          onImageClick={handleOpenLightbox}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* NC Clause Statements Section */}
              <Card variant="outlined" sx={{ borderColor: "error.main", borderWidth: 2 }}>
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <InfoIcon sx={{ fontSize: 28, color: "error.main" }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "error.main" }}>
                        Non-Conformity Clause Statements
                      </Typography>
                      {ncStatements.length > 0 && (
                        <Chip
                          label={`${ncStatements.length} Statements`}
                          size="small"
                          color="error"
                          sx={{ fontWeight: "bold" }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2, borderColor: "error.main" }} />
                  {ncStatementsLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                      <CircularProgress color="error" size={32} />
                    </Box>
                  ) : ncStatements.length > 0 ? (
                    <TableContainer sx={{ maxHeight: 400, border: "1px solid #e0e0e0", borderRadius: 1 }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ bgcolor: "#ffebee", fontWeight: "bold", width: 80 }}>
                              S No.
                            </TableCell>
                            <TableCell sx={{ bgcolor: "#ffebee", fontWeight: "bold", width: 200 }}>
                              Clause No.
                            </TableCell>
                            <TableCell sx={{ bgcolor: "#ffebee", fontWeight: "bold" }}>
                              NC Statement
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ncStatements.map((statement, index) => (
                            <TableRow key={statement.Id} hover>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell>
                                <Chip
                                  label={statement.nc_Clause || "-"}
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  sx={{ fontWeight: "bold", fontSize: "0.7rem" }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: "0.85rem" }}>
                                  {statement.nc_statement || "-"}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: "center", py: 4, bgcolor: "#fafafa", borderRadius: 1 }}>
                      <InfoIcon
                        sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        No Non-Conformity Statements Found
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Button
            onClick={handleCloseDetailsDialog}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #6366f1, #ec4899)",
              color: "white",
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lightbox Modal */}
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxWidth: "90vw",
            maxHeight: "90vh",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.9)",
          },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <IconButton
            onClick={handleCloseLightbox}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "white",
              bgcolor: "rgba(0, 0, 0, 0.5)",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={lightboxImage}
            alt="Equipment"
            style={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
              borderRadius: 8,
            }}
          />
        </Box>
      </Dialog>

      {/* Location Warning Dialog */}
      <LocationWarningDialog
        open={locationWarningOpen}
        onClose={() => setLocationWarningOpen(false)}
        distanceMessage={distanceMessage}
      />

      {/* Hourly Attendance Dialog */}
      <HourlyAttendanceDialog
        open={attendanceCheckDialogOpen}
        onClose={() => setAttendanceCheckDialogOpen(false)}
        message={attendanceCheckMessage}
        onRecheck={checkHourlyAttendance}
      />
    </Container>
    </>
  );
};

// Equipment Image Component
const EquipmentImage = ({ doc, index, onImageClick }) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        if (doc.image) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc.image)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc.image);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc.image);
          }
        }
      } catch (error) {
        console.error("Error loading equipment image:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadImage();
  }, [doc.image]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 120,
          height: 120,
          border: "2px solid #e0e0e0",
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f5f5f5",
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        sx={{
          width: 120,
          height: 120,
          border: "2px solid #e0e0e0",
          borderRadius: 2,
          overflow: "hidden",
          cursor: "pointer",
          transition: "all 0.2s",
          "&:hover": {
            borderColor: "#6366f1",
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")}
      >
        <img
          src={imageUrl}
          alt={`Equipment Image ${index + 1}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
          onError={(e) => {
            e.target.src =
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
          }}
        />
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        align="center"
        sx={{
          fontSize: "0.7rem",
          fontWeight: 500,
        }}
      >
        Image {index + 1}
      </Typography>
    </Box>
  );
};

export default EquipmentDetails;
